import type { CareerId, CriterionId, DepartmentId, ScenarioId, SkillId, ToolId } from './ids';

/** Groupings used by the career exploration interface. Families are descriptive, never a ranking. */
export type CareerFamily =
  | 'it-technical-operations'
  | 'software-engineering'
  | 'systems-business'
  | 'data'
  | 'cloud-security'
  | 'customer-service-delivery'
  | 'it-management-governance';

export const CAREER_FAMILY_LABELS: Record<CareerFamily, string> = {
  'it-technical-operations': 'IT and Technical Operations',
  'software-engineering': 'Software engineering and platforms',
  'systems-business': 'Systems and Business',
  data: 'Data',
  'cloud-security': 'Cloud and Security',
  'customer-service-delivery': 'Customer and Service Delivery',
  'it-management-governance': 'IT Management and Governance',
};

/**
 * `available`: scenario library and workplace tools shipped; full simulation.
 * `planned`: career is browsable for exploration and comparison; simulation content lands incrementally.
 */
export type CareerStatus = 'available' | 'planned';

/** Progression tiers are ordered. They describe demonstrated practice inside the simulator only. */
export type ProgressionTier = 'beginner' | 'foundational' | 'intermediate' | 'advanced';
export const PROGRESSION_TIERS: readonly ProgressionTier[] = ['beginner', 'foundational', 'intermediate', 'advanced'];

/** Skill levels a career expects. Mirrors the three non-beginner progression tiers. */
export type SkillLevel = 'foundational' | 'intermediate' | 'advanced';
export const SKILL_LEVELS: readonly SkillLevel[] = ['foundational', 'intermediate', 'advanced'];

export type SkillCategory =
  | 'technical'
  | 'analysis'
  | 'communication'
  | 'process'
  | 'data'
  | 'security'
  | 'project'
  | 'customer'
  | 'governance';

/**
 * Knowledge domain a skill belongs to. Finer grained than `SkillCategory`, and used by the skill
 * graph, the foundations pathway and the skill map. Descriptive, never a ranking.
 */
export type SkillDomain =
  | 'computing-basics'
  | 'networking'
  | 'operating-systems'
  | 'linux'
  | 'windows'
  | 'it-support'
  | 'security'
  | 'cloud'
  | 'data'
  | 'programming'
  | 'web'
  | 'business-analysis'
  | 'project-delivery'
  | 'communication'
  | 'governance'
  | 'operations';

export const SKILL_DOMAINS: readonly { id: SkillDomain; label: string }[] = [
  { id: 'computing-basics', label: 'Computing basics' },
  { id: 'networking', label: 'Networking' },
  { id: 'operating-systems', label: 'Operating systems' },
  { id: 'linux', label: 'Linux' },
  { id: 'windows', label: 'Windows' },
  { id: 'it-support', label: 'IT support' },
  { id: 'security', label: 'Security' },
  { id: 'cloud', label: 'Cloud' },
  { id: 'data', label: 'Data' },
  { id: 'programming', label: 'Programming' },
  { id: 'web', label: 'Web' },
  { id: 'business-analysis', label: 'Business analysis' },
  { id: 'project-delivery', label: 'Project delivery' },
  { id: 'communication', label: 'Communication' },
  { id: 'governance', label: 'Governance' },
  { id: 'operations', label: 'Operations' },
];

export const SKILL_DOMAIN_LABELS: Record<SkillDomain, string> = Object.fromEntries(SKILL_DOMAINS.map((d) => [d.id, d.label])) as Record<SkillDomain, string>;

export interface SkillDefinition {
  id: SkillId;
  name: string;
  description: string;
  category: SkillCategory;
  /** Knowledge domain, for the skill graph and skill map. */
  domain?: SkillDomain;
  /** Part of the "Must know before you work in tech" foundations pathway. Foundation skills carry `plainEnglish` and `whyItMatters`. */
  foundation?: boolean;
  /** One or two sentences a complete beginner understands. */
  plainEnglish?: string;
  /** Why the skill matters at work, in plain words. */
  whyItMatters?: string;
  /** Skills that must be understood first. Validation rejects unknown ids, self references and cycles. */
  prerequisiteSkillIds?: SkillId[];
  /** Skills that sit close to this one. Treated as symmetric by the skill graph. */
  relatedSkillIds?: SkillId[];
  /** Other names people use for the skill, for search. */
  aliases?: string[];
}

/**
 * Prerequisites are general guidance. `kind` makes the distinction explicit so the UI
 * can label employer-specific items differently from general guidance.
 */
export interface Prerequisite {
  description: string;
  kind: 'general-guidance' | 'commonly-requested' | 'varies-by-employer';
}

export type DeliverableType =
  | 'ticket-resolution-note'
  | 'knowledge-base-article'
  | 'incident-summary'
  | 'requirements-document'
  | 'process-map'
  | 'gap-analysis'
  | 'business-case'
  | 'status-report'
  | 'data-report'
  | 'test-plan'
  | 'change-request'
  | 'runbook'
  | 'risk-register'
  | 'customer-update'
  | 'meeting-notes'
  | 'root-cause-analysis';

export interface EvaluationCriterion {
  id: CriterionId;
  name: string;
  description: string;
  /** Skills that a good result on this criterion provides evidence for. */
  skillIds: SkillId[];
}

export interface ProgressionStage {
  tier: ProgressionTier;
  description: string;
  /** What practice at this stage typically focuses on inside the simulator. */
  typicalFocus: string[];
}

export interface Career {
  id: CareerId;
  name: string;
  family: CareerFamily;
  status: CareerStatus;
  /** Primary department in the virtual company. */
  departmentId: DepartmentId;
  description: string;
  responsibilities: string[];
  skills: Record<SkillLevel, SkillId[]>;
  prerequisites: Prerequisite[];
  /** Tools from the workplace tool registry this career uses. */
  tools: ToolId[];
  deliverables: DeliverableType[];
  evaluationCriteria: EvaluationCriterion[];
  progression: ProgressionStage[];
  relatedCareers: CareerId[];
  /** Optional explicit introductory scenario; otherwise the first `introductory` scenario in the library is used. */
  introScenarioId?: ScenarioId;
  /** Career titles this role commonly grows toward. Descriptive, not a guarantee. */
  commonNextRoles?: string[];
}

/** Text the platform attaches to every career view. Content authors do not need to repeat it. */
export const CAREER_GUIDANCE_NOTICE =
  'Career information here is general guidance. Titles, responsibilities and requirements vary by employer, ' +
  'industry and region. Completing simulations in WorkSim AI demonstrates practice, not qualification: it does not ' +
  'guarantee employment and does not by itself qualify anyone for a professional role.';
